import { Selector } from "@/components/Selector"
import { IOption } from "@/types"
import { Control, Controller, FieldValues, Path } from "react-hook-form"
import { useIntl } from "react-intl"

const toOptions = (length: number, getValue: (index: number) => string): IOption[] =>
  Array.from({ length }, (_, index) => {
    const value = getValue(index)
    return { label: value, value }
  })

const YEARS = toOptions(2015 - 1899, (index) => String(2015 - index))
const MONTHS = "January February March April May June July August September October November December"
  .split(" ")
  .map((label, index) => ({ label, value: String(index + 1).padStart(2, "0") }))

const daysCount = (year?: string, month?: string) => (year && month ? new Date(Number(year), Number(month), 0).getDate() : 31)
const daysOptions = (year?: string, month?: string) => toOptions(daysCount(year, month), (index) => String(index + 1).padStart(2, "0"))

type DatePart = "year" | "month" | "day"
type DateValue = Record<DatePart, string>

const parseDate = (value?: unknown): DateValue => {
  const [year = "", month = "", day = ""] = typeof value === "string" ? value.split("/") : []
  return { year, month, day }
}

interface IDateOfBirthFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  required?: boolean
  label?: string
}

const DateOfBirthField = <T extends FieldValues>({ control, name, required, label = "Date of Birth" }: IDateOfBirthFieldProps<T>) => {
  const { $t: t } = useIntl()

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: (value) => {
          if (!required) return true
          const date = parseDate(value)
          return date.year && date.month && date.day ? true : t({ id: "required" })
        },
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const date = parseDate(value)
        const setPart = (part: DatePart, partValue: string) => {
          const next = { ...date, [part]: partValue }
          if (Number(next.day) > daysCount(next.year, next.month)) next.day = ""
          onChange(`${next.year}/${next.month}/${next.day}`)
        }

        const fields: { part: DatePart; label: string; options: IOption[] }[] = [
          { part: "year", label, options: YEARS },
          { part: "month", label: "\u00A0", options: MONTHS },
          { part: "day", label: "\u00A0", options: daysOptions(date.year, date.month) },
        ]

        return (
          <div className="grid grid-cols-3 gap-2.5 mt-2.5 [&_.selector-custom]:font-normal! mb-2.5">
            {fields.map(({ part, label, options }) => (
              <div key={part} className="col-span-1 [&_.selector-custom]:w-full">
                <label className="mb-1">{label}</label>
                <Selector variant="outlined" options={options} value={date[part]} onChange={(option) => setPart(part, String(option.value))} />
              </div>
            ))}
            {error != null && <span className="col-span-3 text-red-500 text-xs">{error.message}</span>}
          </div>
        )
      }}
    />
  )
}

export default DateOfBirthField
